/**
 * The /j/:joinCode screen. SPEC.md §6.2, §6.3, §6.4, §34.3, §34.4, §34.5.
 *
 * Its first job is to ask nothing. A device that already holds a reconnect token
 * for this table is the same player as before, so it goes straight through to the
 * table and the server restores that identity from the token — a returning player
 * must never produce a second player row, and is never identified by name or
 * device. §6.4
 *
 * Everything else is an edge state whose copy the spec fixes: a locked table
 * (§34.4), a full one, a finished one, and a code that does not resolve at all
 * (§34.5). All of that copy comes from the shared error map. §35
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MAX_PLAYERS,
  formatJoinCode,
  messageForErrorCode,
  truncateName,
  type ErrorCode,
  type PlayerIdentityInput,
} from '@card-counter/shared';
import cinnaLogo from '@/assets/cinna.webp';
import cinnaDrinkLogo from '@/assets/cinna_drink.webp';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  errorCodeOf,
  errorMessageOf,
  fetchSnapshot,
  joinSession,
  lookupSession,
  type SessionLookup,
} from '@/lib/api';
import { readPlayerToken, writePlayerToken } from '@/lib/storage';
import { navigate, paths, useRoute } from '@/router';
import { pushToast } from '@/stores/ui-store';
import { JoinForm } from './JoinForm';

/** Failures that end the visit rather than the attempt. §34.4, §34.5 */
const DEAD_END_CODES: readonly ErrorCode[] = [
  'SESSION_NOT_FOUND',
  'SESSION_LOCKED',
  'SESSION_FULL',
  'SESSION_ENDED',
];

/** Decoration only — the sentence underneath carries the meaning. §19 */
const DEAD_END_GLYPH: Partial<Record<ErrorCode, string>> = {
  SESSION_LOCKED: '🔒',
  SESSION_FULL: '🪑',
  SESSION_ENDED: '🏁',
};

type Phase =
  | { kind: 'checking' }
  | { kind: 'form'; lookup: SessionLookup }
  /** `message` rather than just the code: a network failure carries its own copy. §35 */
  | { kind: 'dead-end'; code: ErrorCode; message: string };

function deadEnd(code: ErrorCode): Phase {
  return { kind: 'dead-end', code, message: messageForErrorCode(code) };
}

/**
 * §34.3: duplicate names are allowed and must never block a join, so the notice
 * arrives after it. Names only exist in the snapshot — the pre-join lookup carries
 * a player count and nothing identifying — so this runs once the device has a
 * token of its own, and a failure is simply dropped.
 */
async function warnOnDuplicateName(
  sessionId: string,
  playerId: string,
  name: string,
): Promise<void> {
  try {
    const snapshot = await fetchSnapshot(sessionId);
    const wanted = name.trim().toLowerCase();
    const clash = snapshot.players.find(
      (player) =>
        player.id !== playerId && !player.isRemoved && player.name.trim().toLowerCase() === wanted,
    );
    if (clash) {
      pushToast({
        id: 'duplicate-name',
        // Player-supplied text, rendered as text and length-capped. §8.6, §39
        message: `Another player is already named ${truncateName(clash.name)}.`,
      });
    }
  } catch {
    // A courtesy warning is not worth reporting a failure for.
  }
}

/**
 * What the header is showing, once a player has picked a character. §6.3, §46
 *
 * Identical in shape and reason to the create screen's, including the nonce: the
 * stretch is a CSS animation, and re-rendering an element whose `animation-name`
 * has not changed does not start it again — so without a fresh key the second
 * pick and every pick after it would swap the artwork with no motion at all.
 */
interface AvatarPreview {
  avatarKey: string;
  nonce: number;
}

export function JoinSessionPage() {
  const route = useRoute();
  const joinCode = route.name === 'join' ? route.joinCode : '';

  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AvatarPreview | null>(null);
  /**
   * Avatars the lookup did not know about, learned by being refused one. §46
   *
   * The taken list is fetched once with the table and does not stream, so two
   * people picking the same character at the same moment is a race only the
   * server can settle. When it does, the loser's picker has to actually change —
   * an error message beside a grid that still offers the character it just
   * refused reads as a bug in the app rather than as somebody being quicker.
   */
  const [refusedAvatarKeys, setRefusedAvatarKeys] = useState<readonly string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    setPhase({ kind: 'checking' });
    setJoinError(null);
    // A different code is a different table, so a character previewed against
    // the last one is not what the form underneath now holds — and neither is
    // anything that table happened to refuse. §6.3, §46
    setPreview(null);
    setRefusedAvatarKeys([]);

    void (async () => {
      try {
        const lookup = await lookupSession(joinCode, controller.signal);
        if (controller.signal.aborted) return;

        // Already a player here: no name, no avatar, no second row. §6.4
        // Checked before locked/full/ended on purpose — an existing player may
        // reconnect to a locked table (§27.1), and an ended game still has final
        // scores worth showing them (§52).
        if (readPlayerToken(lookup.sessionId)) {
          navigate(paths.game(lookup.sessionId), { replace: true });
          return;
        }

        if (lookup.status === 'ended') {
          setPhase(deadEnd('SESSION_ENDED'));
          return;
        }
        if (lookup.locked) {
          setPhase(deadEnd('SESSION_LOCKED'));
          return;
        }
        // The soft cap is a UI limit, and saying so beats letting someone pick a
        // name they cannot use. The server decides for real. §45
        if (lookup.playerCount >= MAX_PLAYERS) {
          setPhase(deadEnd('SESSION_FULL'));
          return;
        }
        setPhase({ kind: 'form', lookup });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setPhase({ kind: 'dead-end', code: errorCodeOf(err), message: errorMessageOf(err) });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [joinCode, attempt]);

  const retry = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  async function submit(identity: PlayerIdentityInput): Promise<void> {
    setSubmitting(true);
    setJoinError(null);
    try {
      const credentials = await joinSession(joinCode, identity);
      writePlayerToken(credentials.sessionId, credentials.playerToken);
      navigate(paths.game(credentials.sessionId), { replace: true });
      // Deliberately after the navigation: the join has already succeeded, and a
      // warning must not stand between a player and the table. §34.3
      void warnOnDuplicateName(credentials.sessionId, credentials.playerId, identity.name);
    } catch (err) {
      setSubmitting(false);
      const code = errorCodeOf(err);
      // The table changed under us between the lookup and the tap.
      if (DEAD_END_CODES.includes(code)) {
        setPhase({ kind: 'dead-end', code, message: errorMessageOf(err) });
        return;
      }
      // Somebody was quicker. Recording it here is what moves the form off that
      // character and greys it out, so "pick another" has somewhere to go. §46
      if (code === 'AVATAR_TAKEN') {
        setRefusedAvatarKeys((current) =>
          current.includes(identity.avatarKey) ? current : [...current, identity.avatarKey],
        );
      }
      setJoinError(errorMessageOf(err));
    }
  }

  const display = formatJoinCode(joinCode);

  /**
   * What the table had when we looked it up, plus anything it has refused us
   * since. §46
   *
   * Memoised on both, so the picker is handed the same array between renders and
   * JoinForm's "move off a taken avatar" effect does not re-run on every
   * keystroke in the name field. §44
   */
  const lookedUp = phase.kind === 'form' ? phase.lookup.takenAvatarKeys : undefined;
  const taken = useMemo(
    () => [...new Set([...(lookedUp ?? []), ...refusedAvatarKeys])],
    [lookedUp, refusedAvatarKeys],
  );

  return (
    // Centred when it fits, scrolled when it does not — the avatar grid plus a
    // software keyboard easily outgrows a short viewport. §18.2, §43
    <main className="scroll-area flex min-h-0 flex-1 flex-col p-safe">
      <div className="m-auto flex w-full max-w-sm flex-col gap-6">
        {phase.kind === 'checking' ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <Spinner size="lg" label="Checking that table…" />
            <p className="text-ink-muted">Checking that table…</p>
          </div>
        ) : null}

        {phase.kind === 'form' ? (
          <>
            <header className="flex flex-col items-center gap-1 text-center">
              {/*
                The same preview the create screen has, at the same size and for
                the same reason: the character is what a player is choosing, and
                it was previously a 48 px tile in a grid of 112. This box
                is what has the height — the artwork inside it changes from a
                wide logo to a square sticker, and a container sized by its
                contents would jump by the difference at the moment of the swap,
                under the stretch, which is the one moment nothing else may move.
                §6.3, §18.2, §46
              */}
              <div className="mb-1 flex h-48 w-full items-end justify-center sm:h-52">
                {preview ? (
                  <Avatar
                    // Remounted per pick — see AvatarPreview. §6.3
                    key={preview.nonce}
                    avatarKey={preview.avatarKey}
                    className="avatar-preview-stretch h-4/5 w-auto max-w-full"
                  />
                ) : (
                  /* Decorative — the code below it is what names this screen.
                     Which Cinnamoroll says which game the table is playing, so a
                     player knows they are sitting down to drinking mode before
                     they pick a name for it. §19, §4.6 */
                  <img
                    src={phase.lookup.drinkingMode ? cinnaDrinkLogo : cinnaLogo}
                    alt=""
                    aria-hidden="true"
                    width={640}
                    height={340}
                    decoding="async"
                    className="h-full w-auto max-w-[70%] object-contain object-bottom"
                  />
                )}
              </div>
              <p className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">
                Joining
              </p>
              <p data-numeric className="text-2xl font-bold tracking-[0.18em] text-ink">
                {display}
              </p>
              <p className="text-sm text-ink-muted">
                {phase.lookup.playerCount === 1
                  ? '1 player at the table'
                  : `${String(phase.lookup.playerCount)} players at the table`}
                {/* Stated in words, never by the artwork alone. §19 */}
                {phase.lookup.drinkingMode ? ' · drinking mode' : ''}
              </p>
            </header>
            <JoinForm
              submitLabel="JOIN TABLE"
              submitting={submitting}
              error={joinError}
              takenAvatarKeys={taken}
              onAvatarChange={(avatarKey) => {
                setPreview((current) => ({ avatarKey, nonce: (current?.nonce ?? 0) + 1 }));
              }}
              onSubmit={(identity) => {
                void submit(identity);
              }}
            />
          </>
        ) : null}

        {phase.kind === 'dead-end' ? (
          <section className="flex flex-col items-center gap-5 text-center">
            <p className="text-5xl" aria-hidden="true">
              {DEAD_END_GLYPH[phase.code] ?? '🃏'}
            </p>
            <div className="flex flex-col gap-2">
              <h1 data-numeric className="text-xl font-semibold tracking-[0.15em] text-ink">
                {display}
              </h1>
              <p role="status" className="text-ink-muted">
                {phase.message}
              </p>
            </div>

            <div className="flex w-full flex-col gap-2">
              {/* A code that does not resolve will not resolve on a retry. §34.5 */}
              {phase.code === 'SESSION_NOT_FOUND' ? null : (
                <Button variant="secondary" fullWidth onClick={retry}>
                  Try again
                </Button>
              )}
              <Button
                fullWidth
                onClick={() => {
                  navigate('/?create');
                }}
              >
                Create Game
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={() => {
                  navigate(paths.landing());
                }}
              >
                Enter a different code
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}
